import classNames from "classnames";
import { motion } from "framer-motion";
import { CSSProperties, useEffect, useMemo, useState } from "react";
import { useSetRecoilState } from "recoil";
import Colors from "~/constants/colors";
import { AlertAtom } from "~/services/alerts";
import Gift from "./gift";

export interface PromotionThreshold {
  discount: string;
  subtotal: number;
}

export interface ProgressProps {
  currentSubtotal: number;
  promotionThresholds: PromotionThreshold[];
  isSamePromotions: boolean;
}

const checkIsNumber = (discount: string) => {
  return !Number.isNaN(Number(discount?.replace("%", "")?.replace("$", "")));
};

const Progress = ({
  currentSubtotal,
  promotionThresholds = [],
  isSamePromotions,
}: ProgressProps) => {
  const [nextIndexActive, setNextIndexActive] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const setAlert = useSetRecoilState(AlertAtom.alerts);

  const formattedPromotionThresholds = useMemo(() => {
    if (!promotionThresholds.length) return [];
    return promotionThresholds
      .map((threshold) => ({
        ...threshold,
        subtotal: Number(threshold.subtotal),
      }))
      ?.filter((threshold) => !Number.isNaN(threshold.subtotal))
      ?.sort((a, b) => a.subtotal - b.subtotal);
  }, [promotionThresholds]);

  const displayPromotionThresholds = useMemo(() => {
    if (!formattedPromotionThresholds.length) return [];
    const result = formattedPromotionThresholds.slice(
      currentIndex,
      currentIndex + 3
    );
    return result;
  }, [formattedPromotionThresholds, currentIndex]);

  const maxSubtotal = Math.max(
    ...formattedPromotionThresholds.map((threshold) => threshold.subtotal)
  );

  const nextPromotions = formattedPromotionThresholds?.[nextIndexActive];
  const nextSubtotal = nextPromotions?.subtotal;
  const nextIsDiscountNumber = checkIsNumber(nextPromotions?.discount);

  const maxSubtotalIntoThree = Math.max(
    ...displayPromotionThresholds.map((threshold) => threshold.subtotal)
  );
  const progressPercentage =
    currentSubtotal >= maxSubtotalIntoThree
      ? 100
      : (currentSubtotal / maxSubtotalIntoThree) * 100;

  useEffect(() => {
    if (!!isSamePromotions) {
      setCurrentIndex(0);
      setNextIndexActive(0);
      return;
    }
    if (!formattedPromotionThresholds.length) return;
    if (!currentSubtotal) return;
    if (currentSubtotal >= maxSubtotal) {
      setCurrentIndex(formattedPromotionThresholds.length - 3);
      return;
    }

    let tempNextIndexActive = formattedPromotionThresholds.findLastIndex(
      (threshold, index) => {
        if (currentSubtotal >= threshold.subtotal) {
          return true;
        }
      }
    );
    if (tempNextIndexActive >= 0) {
      setCurrentIndex(tempNextIndexActive);
    }
    //Check new index is greater than current index
    if (tempNextIndexActive + 1 < formattedPromotionThresholds.length) {
      setNextIndexActive(tempNextIndexActive + 1);
    }
  }, [currentSubtotal, formattedPromotionThresholds, isSamePromotions]);

  useEffect(() => {
    if (!!isSamePromotions) return;

    if (currentSubtotal > 0 && maxSubtotal > 0) {
      const currentReward = formattedPromotionThresholds.findLastIndex(
        (threshold) => {
          return currentSubtotal >= threshold.subtotal;
        }
      );

      if (currentReward != -1) {
        setAlert({
          open: true,
          message: `You've unlocked a reward of <b style="color:${Colors.PRIMARY}">${formattedPromotionThresholds[currentReward].discount}</b> off your order`,
          type: "success",
        });
      }
    }
  }, [currentIndex, currentSubtotal, isSamePromotions]);

  return (
    <div
      className={`w-full rounded-full transition-all mb-2 mt-8`}
      style={
        {
          "--primary-color": Colors.PRIMARY,
          "--light-color": Colors.PRIMARY_LIGHT,
        } as CSSProperties
      }
    >
      <motion.div
        className="flex justify-between relative w-full h-2 bg-gray-200 rounded-full"
        animate={
          currentSubtotal >= maxSubtotal
            ? { y: [0, -4, 0], rotate: [0, 6, -6, 0] }
            : {}
        }
        transition={{
          y: {
            duration: 1,
          },
          rotate: {
            duration: 0.5,
          },
        }}
      >
        {formattedPromotionThresholds.map((threshold, index) => {
          const subtotal = threshold.subtotal;
          const discount = threshold.discount;
          const isDisplay =
            displayPromotionThresholds.findIndex(
              (threshold) => threshold.subtotal === subtotal
            ) !== -1;
          const isBefore = index < currentIndex;

          const thresholdPercentage = isDisplay
            ? (subtotal / maxSubtotalIntoThree) * 100
            : isBefore
              ? 0
              : 100;
          const active = currentSubtotal >= subtotal;

          const lastItem = index === formattedPromotionThresholds.length - 1;
          const isDiscountNumber = checkIsNumber(discount);
          return (
            <div
              key={index}
              className="absolute top-0 w-full h-full transition-all ease-in-out"
            >
              <div
                className={`text-sm text-end h-full relative transition-all ease-in-out duration-300 ${
                  active ? "text-primary" : "text-gray-500"
                }`}
                style={{
                  width: `${thresholdPercentage}%`,
                  opacity: isDisplay ? 1 : 0,
                }}
              >
                <div
                  className={classNames(
                    "absolute right-0 bottom-0 translate-y-1/4 cursor-pointer flex justify-center items-center rounded-full z-10 ",
                    {
                      ["translate-x-[5px]"]: !!lastItem,
                    }
                  )}
                >
                  <div
                    className={classNames("absolute -top-6 text-nowrap", {
                      "right-0": !!lastItem,
                    })}
                  >
                    {isDiscountNumber ? <span>-{discount}</span> : discount}
                  </div>
                  <span
                    className="p-1 rounded-full"
                    style={{
                      backgroundColor: active
                        ? "var(--primary)"
                        : "var(--light-color)",
                    }}
                  >
                    <Gift isActive={active} discount={discount} />
                  </span>
                </div>
              </div>
            </div>
          );
        })}
        <div>
          <motion.div
            className={classNames(
              "absolute top-0 left-0 h-full rounded-full cursor-pointer max-w-full ",
              {
                "bg-primary": !isSamePromotions,
              }
            )}
            animate={{
              width: !isSamePromotions ? `${progressPercentage}%` : "0",
            }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </motion.div>
      <div className=" w-full mt-2 justify-center flex text-sm md:text-base">
        {!!isSamePromotions ? (
          "You can only apply one discount program at a time"
        ) : (
          <>
            {currentSubtotal < maxSubtotal && (
              <div className="flex gap-1 wrap text-sm">
                <div className="text-nowrap">
                  Spend{" "}
                  <span
                    className="font-semibold"
                    style={{
                      color: "var(--primary-color)",
                    }}
                  >
                    ${(nextSubtotal - currentSubtotal).toFixed(2)}
                  </span>{" "}
                  more to receive{" "}
                </div>
                {nextIsDiscountNumber ? (
                  <span className="flex gap-1">
                    <span
                      className="font-semibold"
                      style={{
                        color: "var(--primary-color)",
                      }}
                    >
                      {" "}
                      {nextPromotions?.discount}{" "}
                    </span>{" "}
                    OFF{" "}
                    <span className="hidden md:block">on sub total order</span>
                  </span>
                ) : (
                  <span className="font-bold"> {nextPromotions?.discount}</span>
                )}
              </div>
            )}
            {currentSubtotal >= maxSubtotal &&
              "Congratulations! You've unlocked all the rewards. Enjoy your perks!"}
          </>
        )}
      </div>
    </div>
  );
};

export default Progress;
